import React from 'react';

interface MagicSelectControlProps {
  active: boolean;
  loading: boolean;
  threshold: number;
  mode: 'forest' | 'deforested';
  rightOffset?: number;
  onThresholdChange: (value: number) => void;
  onModeChange: (mode: 'forest' | 'deforested') => void;
  onCancel: () => void;
}

export const MagicSelectControl: React.FC<MagicSelectControlProps> = ({
  active,
  loading,
  threshold,
  mode,
  rightOffset = 340,
  onThresholdChange,
  onModeChange,
  onCancel
}) => {
  if (!active) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: '20px',
      right: `${rightOffset}px`,
      background: 'rgba(26, 38, 66, 0.95)',
      color: 'white',
      padding: '12px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      zIndex: 1000,
      width: '200px',
      border: '1px solid #c9a961',
      pointerEvents: 'auto'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontWeight: 600, color: '#c9a961', fontSize: '13px' }}>
          {loading ? 'Analyzing...' : 'Magic Select'}
        </span>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '0 2px'
          }}
          title="Cancel magic select"
        >&times;</button>
      </div>

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '10px 0',
          color: '#c9a961'
        }}>
          <div style={{ fontSize: '20px', animation: 'spin 1s linear infinite' }}>&#9881;</div>
          <div style={{ marginTop: '6px', fontSize: '11px' }}>Fetching NDVI data...</div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {!loading && (
        <>
          {/* Mode toggle */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ marginBottom: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>Detection mode:</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => onModeChange('forest')}
                style={{
                  flex: 1,
                  background: mode === 'forest' ? '#228B22' : 'rgba(255,255,255,0.08)',
                  color: mode === 'forest' ? 'white' : 'rgba(255,255,255,0.6)',
                  border: `1px solid ${mode === 'forest' ? '#228B22' : 'rgba(255,255,255,0.2)'}`,
                  borderRadius: '4px',
                  padding: '4px 6px',
                  fontSize: '11px',
                  fontWeight: mode === 'forest' ? 700 : 400,
                  cursor: 'pointer'
                }}
              >Forest</button>
              <button
                onClick={() => onModeChange('deforested')}
                style={{
                  flex: 1,
                  background: mode === 'deforested' ? '#DAA520' : 'rgba(255,255,255,0.08)',
                  color: mode === 'deforested' ? '#1a2642' : 'rgba(255,255,255,0.6)',
                  border: `1px solid ${mode === 'deforested' ? '#DAA520' : 'rgba(255,255,255,0.2)'}`,
                  borderRadius: '4px',
                  padding: '4px 6px',
                  fontSize: '11px',
                  fontWeight: mode === 'deforested' ? 700 : 400,
                  cursor: 'pointer'
                }}
              >Deforested</button>
            </div>
          </div>

          {/* Threshold slider */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>NDVI threshold:</span>
              <span style={{ color: '#c9a961', fontWeight: 600, fontSize: '11px' }}>{threshold.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="0.8"
              step="0.05"
              value={threshold}
              onChange={e => onThresholdChange(parseFloat(e.target.value))}
              style={{
                width: '100%',
                height: '4px',
                accentColor: '#c9a961'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
              <span>0.1</span>
              <span>0.8</span>
            </div>
          </div>

          {/* Instructions */}
          <div style={{
            fontSize: '11px',
            color: 'rgba(255,255,255,0.5)',
            lineHeight: '1.4',
            borderTop: '1px solid rgba(201,169,97,0.3)',
            paddingTop: '8px'
          }}>
            Click a point inside a territory to auto-detect {mode === 'forest' ? 'forested' : 'deforested'} area
          </div>
        </>
      )}
    </div>
  );
};
